import { useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { initSectionVisibilityObserver } from "@/utils/analytics";
import { useSEO } from "@/hooks/useSEO";
import { HOME_PAGE_SEO } from "@/constants/seoConfig";
import {
  HOME_SECTION_IDS,
  scrollToHomeSection,
  sectionIdFromPathname,
} from "@/constants/homeSections";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Problem from "./components/Problem";
import EmotionalHit from "./components/EmotionalHit";
import LiveDemo from "./components/LiveDemo";
import UseCases from "./components/UseCases";
import TrustSafety from "./components/TrustSafety";
import WhyAva from "./components/WhyAva";
import SocialProof from "./components/SocialProof";
import Pricing from "./components/Pricing";
import FAQ from "./components/FAQ";
import FinalCTA from "./components/FinalCTA";
import Footer from "./components/Footer";
import AnimatedBackground from "./components/AnimatedBackground";

export default function Home() {
  const pageRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const homeSeo = useMemo(() => HOME_PAGE_SEO, []);
  useSEO(homeSeo);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    const hashSection = location.hash.replace(/^#/, "");
    const pathSection = sectionIdFromPathname(location.pathname);
    const sectionId = pathSection || hashSection || null;

    if (sectionId) {
      const timer = window.setTimeout(() => {
        scrollToHomeSection(sectionId);
      }, 150);
      return () => window.clearTimeout(timer);
    }

    if (location.pathname === "/") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transition = "opacity 0.6s ease";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity = "1";
      });
    });
  }, []);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      cleanup = initSectionVisibilityObserver([...HOME_SECTION_IDS]);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      cleanup?.();
    };
  }, []);

  return (
    <div ref={pageRef} className="font-sans relative">
      <AnimatedBackground />
      <div className="relative z-10">
        <Navbar />
        <Hero />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <Problem />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
        <EmotionalHit />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <LiveDemo />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
        <UseCases />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <TrustSafety />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
        <WhyAva />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <SocialProof />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
        <Pricing />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <FAQ />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
        <FinalCTA />
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />
        <Footer />
      </div>
    </div>
  );
}
